import React from 'react';
import {
    Button,
    Textarea,
    Field,
    // Radio,
    // RadioGroup
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { navigateSafely } from '../../../utils/navigation';
import RequiredLabel from './RequiredLabel';
import { ArchiveDropdown, CategoryDropdown, CaseTagPicker, RecordTypeDropdown, CarbonCopyPicker } from '../components';
import { QuickSaveState } from '../hooks/useQuickSaveState';
import { FormState } from '../hooks/useFormState';
import { ISaveInfo, DateOnly, unrestrictedAccess } from '../model/QuickSaveModel';
import { isQuickSaveFormComplete } from '../utils/formValidation';

interface QuickSaveFormProps {
    state: QuickSaveState;
    formState?: FormState;
}
export const QuickSaveForm: React.FC<QuickSaveFormProps> = ({ state, formState }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isFormComplete = isQuickSaveFormComplete(state);
    const canSubmitDirectly = state.caseValidation?.AccessProperties?.ArchiveHasAccessCode === 0
        && state.caseValidation?.AccessProperties?.ArchiveHasAccessGroup === 0;

    const handleSubmitClick = () => {
        if (formState?.handleSubmit) {
            formState.handleSubmit();
        } else {
            state.handleSubmit();
        }
    };

    const handleReviewAccessRights = () => {
        const accessProps = state.caseValidation?.AccessProperties ?? unrestrictedAccess;
        const saveInfo: ISaveInfo = {
            documentArchive: state.selectedArchive?.Recno.toString() || '',
            documentCategory: state.selectedCategory?.Recno.toString() || '',
            caseReno: state.selectedCase?.Recno.toString() || null,
            documentTitle: state.title,
            ccContact: state.selectedCopyTo.length > 0 ? state.selectedCopyTo.map(contact => ({
                Recno: contact.Recno,
                Name: contact.Name,
                IsInternal: contact.IsInternal,
                Domain: contact.Domain,
                EmployerRecno: contact.EmployerRecno || 0,
                EmployerName: contact.EmployerName,
                Address: contact.Address,
                Zip: contact.Zip
            })) : null,
            recordType: state.selectedRecordType ? state.selectedRecordType.Recno.toString() : null,
            accessProps,
            documentDate: DateOnly.Today()
        };
        const preservedState = {
            selectedArchive: state.selectedArchive ?? undefined,
            selectedCategory: state.selectedCategory ?? undefined,
            selectedCase: state.selectedCase ?? undefined,
            selectedRecordType: state.selectedRecordType ?? undefined,
            selectedCopyTo: state.selectedCopyTo,
            title: state.title,
            isRestricted: true
        };
        navigateSafely(navigate, '/accessReview', { state: { saveInfo, preservedState } });
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e0e0e0'
        }}>
            <Field label={<RequiredLabel text={t("Title")} required={true} />}>
                <Textarea
                    value={state.title}
                    onChange={state.onChangeTitle}
                    placeholder={t("Enter document title")}
                />
            </Field>

            <ArchiveDropdown
                archives={state.archives}
                selectedArchive={state.selectedArchive}
                isLoading={state.isLoadingArchives}
                error={state.error}
                required={true}
                onArchiveChange={state.handleArchiveChange}
            />

            {state.caseConnection !== 0 && state.selectedArchive && (
                <CaseTagPicker
                    key={state.selectedArchive.Recno} // Force re-mount when archive changes
                    selectedCase={state.selectedCase}
                    selectedArchive={state.selectedArchive}
                    onCaseChange={state.handleCaseChange}
                    isLoading={state.isValidatingCase}
                    multiSelect={false}
                    required={true}
                    hideCoin={false}
                />
            )}

            {state.caseValidation && !state.caseValidation.CaseIsValid && (
                <Field
                    validationMessage={state.caseValidation.CaseValidationMessage}
                    validationState="error"
                >
                    <div /> {/* Empty div as Field requires a child */}
                </Field>
            )}

            {state.caseValidation && !state.caseValidation.ArchiveIsValid && (
                <Field
                    validationMessage={state.caseValidation.ArchiveValidationMessage}
                    validationState="error"
                >
                    <div /> {/* Empty div as Field requires a child */}
                </Field>
            )}

            {state.showRecordTypes && (
                <RecordTypeDropdown
                    selectedCase={state.selectedCase}
                    selectedArchive={state.selectedArchive}
                    selectedRecordType={state.selectedRecordType}
                    onRecordTypeChange={state.handleRecordTypeChange}
                />
            )}

            <CategoryDropdown
                categories={state.categories}
                selectedCategory={state.selectedCategory}
                isLoading={state.isLoadingCategories}
                error={state.categoryError}
                disabled={!state.selectedArchive || Boolean(state.selectedRecordType)}
                required={true}
                onCategoryChange={state.handleCategoryChange}
            />

            {state.showCcContacts && (
                <CarbonCopyPicker
                    selectedContacts={state.selectedCopyTo}
                    onContactsChange={state.handleCopyToChange}
                    caseNumber={state.selectedCase?.Recno}
                />
            )}

            {/* Radio group disabled: access review is always shown unless a record type is selected
            {!state.showRecordTypes && (
                <Field label={t("Access code")}>
                    <RadioGroup
                        value={formState?.isRestricted ? "restricted" : "unrestricted"}
                        onChange={(_, data) => {
                            if (formState?.setIsRestricted) {
                                formState.setIsRestricted(data.value === "restricted");
                            }
                        }}
                    >
                        <Radio value="unrestricted" label={t('Unrestricted')} />
                        <Radio value="restricted" label={t('Restricted')} />
                    </RadioGroup>
                </Field>
            )}
            */}

            {formState?.submitError && (
                <div style={{
                    color: 'red',
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#ffebee',
                    borderRadius: '4px'
                }}>
                    {formState.submitError}
                </div>
            )}

            {canSubmitDirectly ? (
                <Button
                    appearance="primary"
                    onClick={handleSubmitClick}
                    disabled={formState?.isSubmitting || !isFormComplete}
                    style={{ marginTop: '16px', alignSelf: 'center', paddingLeft: '32px', paddingRight: '32px' }}
                >
                    {t('Submit')}
                </Button>
            ) : (
                <Button
                    appearance="primary"
                    onClick={handleReviewAccessRights}
                    disabled={!isFormComplete}
                    style={{ marginTop: '16px', alignSelf: 'center', paddingLeft: '32px', paddingRight: '32px' }}
                >
                    {t('Review access rights')}
                </Button>
            )}
        </div>
    );
};
